<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-gray-800">Aufträge importieren</h2>
        <button @click="$emit('close')" class="text-gray-700 hover:text-black text-3xl iconbutton admin-button--ghost admin-button--icon" aria-label="Schließen" title="Schließen">
          <i class="fa fa-xmark"></i>
        </button>
      </div>

      <div class="mb-6">
        <label class="block text-sm font-semibold mb-2">Import-Modus:</label>
        <select v-model="importMode" class="border rounded px-3 py-2 w-full">
          <option value="">-- Modus auswählen --</option>
          <option v-for="mode in availableModes" :key="mode" :value="mode">{{ mode }}</option>
        </select>
      </div>

      <div v-if="importMode" class="mb-6 flex gap-3 items-center">
        <button
          @click="downloadTemplate"
          class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 admin-button--secondary"
        >
          <i class="fa fa-download mr-2"></i>Vorlage herunterladen
        </button>
        <input type="checkbox" id="downloadWithJobs" v-model="downloadWithJobs" />
        <label for="downloadWithJobs" class="text-sm text-gray-700 cursor-pointer select-none">Mit aktuellen Aufträgen als Referenz</label>
      </div>

      <div v-if="importMode" class="mb-6">
        <label class="block text-sm font-semibold mb-2">CSV/XLSX-Datei:</label>
        <input
          type="file"
          @change="handleFileUpload"
          accept=".csv,.xlsx,.xls,.xlsm"
          class="border rounded px-3 py-2 w-full"
        />
      </div>

      <div v-if="summary" class="mb-4 p-3 rounded bg-gray-100 border border-gray-300">
        <span class="font-semibold">{{ summary.total }} Zeilen</span> |
        <span class="text-green-700">{{ summary.ok }} OK</span> |
        <span class="text-yellow-700">{{ summary.warnings }} Warnungen</span> |
        <span class="text-red-700">{{ summary.errors }} Fehler</span>
      </div>

      <div v-if="importedJobs.length > 0" class="mb-4">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b-2 border-gray-400">
              <th class="text-left p-1">#</th>
              <th class="text-left p-1">Status</th>
              <th class="text-left p-1">Mitarbeiter</th>
              <th class="text-left p-1">PSL-Nr.</th>
              <th class="text-left p-1">Datum</th>
              <th class="text-left p-1">Meldungen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(job, i) in importedJobs"
              :key="i"
              :class="{
                'bg-red-50': job._status === 'error',
                'bg-yellow-50': job._status === 'warning',
                'bg-green-50': job._status === 'ok'
              }"
            >
              <td class="p-1">{{ i + 1 }}</td>
              <td class="p-1">
                <span v-if="job._status === 'error'" class="text-red-700 font-bold">Fehler</span>
                <span v-else-if="job._status === 'warning'" class="text-yellow-700 font-bold">Warnung</span>
                <span v-else class="text-green-700 font-bold">OK</span>
              </td>
              <td class="p-1">{{ job.Mitarbeiter_Vorname || '' }} {{ job.Mitarbeiter_Nachname || '' }}</td>
              <td class="p-1">{{ job.Mitarbeiternr || '' }}</td>
              <td class="p-1">{{ job.Datum_von || '' }}</td>
              <td class="p-1 text-xs">
                <div v-for="msg in job._messages" :key="msg">{{ msg }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="errorMessage" class="mb-4 p-3 rounded bg-red-100 border border-red-400 text-red-700">
        {{ errorMessage }}
      </div>

      <div v-if="importedJobs.length > 0" class="flex justify-end gap-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 rounded border border-gray-400 hover:bg-gray-100 admin-button--secondary"
        >Abbrechen</button>
        <button
          @click="confirmImport"
          :disabled="hasErrors"
          :class="[
            'px-4 py-2 rounded font-semibold text-white admin-button--primary',
            hasErrors ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          ]"
        >
          <i class="fa fa-check mr-2"></i>Import ({{ importedJobs.length }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import Fuse from 'fuse.js';
import views from '../lib/views.json';

// Columns that are restricted to known values (dropdowns) and need validation.
// Maps column key -> validator config.
const DROPDOWN_VALIDATORS = {
  Vertragsbeschreibung: { source: 'contracts', label: 'Vertragsbeschreibung' },
  Einsatzort_Beschreibung: { source: 'contractLocations', label: 'Einsatzort' },
  Einsatzstelle_Beschreibung: { source: 'contractPlaces', label: 'Einsatzstelle' },
  Firmenkurzel: { source: 'companies', label: 'Firmenkürzel' },
  Leistungsart_Code: { source: 'servicetypes', label: 'Leistungsart' }
};

// Columns that are readonly but used for employee lookup (not greyed out in template)
const NAME_COLUMNS = ['Mitarbeiter_Vorname', 'Mitarbeiter_Nachname'];

export default {
  name: 'JobImportModal',
  props: {
    allUsers: {
      type: Array,
      required: true
    },
    allContracts: {
      type: Array,
      default: () => []
    },
    allCompanies: {
      type: Array,
      default: () => []
    },
    allServicetypes: {
      type: Array,
      default: () => []
    },
    currentJobs: {
      type: Array,
      default: () => []
    },
    currentMode: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'import'],
  data() {
    return {
      importedJobs: [],
      errorMessage: '',
      downloadWithJobs: false
    };
  },
  computed: {
    availableModes() {
      return Object.keys(views);
    },
    importMode: {
      get() {
        return this._importMode || this.currentMode || '';
      },
      set(val) {
        this._importMode = val;
      }
    },
    currentColumns() {
      return views[this.importMode] || [];
    },
    summary() {
      if (this.importedJobs.length === 0) return null;
      const ok = this.importedJobs.filter(j => j._status === 'ok').length;
      const warnings = this.importedJobs.filter(j => j._status === 'warning').length;
      const errors = this.importedJobs.filter(j => j._status === 'error').length;
      return { total: this.importedJobs.length, ok, warnings, errors };
    },
    hasErrors() {
      return this.importedJobs.some(j => j._status === 'error');
    }
  },
  methods: {
    // ── Template download ──────────────────────────────────────────────
    downloadTemplate() {
      const cols = this.currentColumns;
      if (!cols.length) return;

      // Mark readonly columns (except name columns) with a suffix since
      // xlsx community edition does not support cell styling (fills/fonts)
      const headerRow = cols.map(c => {
        const isNameCol = NAME_COLUMNS.includes(c.column);
        if (c.readonly && !isNameCol) {
          return c.name + ' (readonly)';
        }
        return c.name;
      });
      const dataRows = [];

      // Optionally include current jobs as reference entries
      if (this.downloadWithJobs && this.currentJobs.length > 0) {
        this.currentJobs.forEach(job => {
          const row = cols.map(col => {
            const val = job[col.column];
            if (val == null) return '';
            if (col.type === 'checkbox') return val ? 'x' : '';
            return val;
          });
          dataRows.push(row);
        });
      }

      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

      // Set column widths based on header length (account for readonly suffix)
      ws['!cols'] = cols.map(c => {
        const isNameCol = NAME_COLUMNS.includes(c.column);
        const headerLen = (c.readonly && !isNameCol) ? c.name.length + 12 : c.name.length;
        return { wch: Math.max(headerLen + 2, 12) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Import');
      XLSX.writeFile(wb, `import_vorlage_${this.importMode}.xlsx`);
    },

    // ── File upload & parsing ──────────────────────────────────────────
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.errorMessage = '';
      this.importedJobs = [];

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', raw: false });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
          this.parseRows(rows);
        } catch (error) {
          this.errorMessage = 'Fehler beim Lesen der Datei: ' + error.message;
        }
      };
      reader.onerror = () => {
        this.errorMessage = 'Fehler beim Lesen der Datei.';
      };
      reader.readAsArrayBuffer(file);
    },

    parseRows(rows) {
      if (rows.length < 2) {
        this.errorMessage = 'Die Datei enthält keine Daten';
        return;
      }

      const cols = this.currentColumns;
      if (!cols.length) {
        this.errorMessage = 'Kein Modus gewählt';
        return;
      }

      // Build header-name → column-key mapping from the view definition
      const nameToColumn = {};
      cols.forEach(c => {
        nameToColumn[c.name] = c.column;
      });

      // Read header row from file and map to column keys
      // Strip the ' (readonly)' suffix that was added during template generation
      const fileHeaders = (rows[0] || []).map(h => {
        if (h == null) return '';
        return String(h).trim().replace(/\s*\(readonly\)\s*$/, '');
      });
      const colIndexMap = {};
      fileHeaders.forEach((h, i) => {
        if (nameToColumn[h]) {
          colIndexMap[nameToColumn[h]] = i;
        }
      });

      const dataRows = rows.slice(1);
      const fuseByName = new Fuse(this.allUsers, {
        keys: ['name'],
        threshold: 0.4,
        ignoreLocation: true
      });
      const fuseBySurname = new Fuse(this.allUsers, {
        keys: ['surname'],
        threshold: 0.4,
        ignoreLocation: true
      });

      dataRows.forEach(row => {
        if (!row || row.length === 0) return;
        const job = this.parseRow(row, colIndexMap, cols, fuseByName, fuseBySurname);
        if (job) this.importedJobs.push(job);
      });

      if (this.importedJobs.length === 0) {
        this.errorMessage = 'Keine Datenzeilen gefunden. Bitte überprüfen Sie Datei und Modus.';
      }
    },

    parseRow(row, colIndexMap, cols, fuseByName, fuseBySurname) {
      const job = { _messages: [], _status: 'ok', new: true, Zeiterf_Buch_Blattname: this.importMode.toUpperCase() };

      // Extract all column values
      cols.forEach(col => {
        const idx = colIndexMap[col.column];
        if (idx == null) return;

        // Parse Debitorname as a string and only pass it on if a value is given
        if (col.column === 'Debitorname') {
          const value = this.trimValue(row[idx]);
          if (value) job[col.column] = value;
          return;
        }

        let rawValue = row[idx];
        if (col.type === 'checkbox') {
          job[col.column] = this.parseCheckbox(rawValue);
        } else if (col.type === 'date') {
          job[col.column] = this.parseDate(rawValue);
        } else if (col.type === 'time') {
          job[col.column] = this.parseTime(rawValue, col.column === 'Soll_Bis');
        } else if (col.type === 'number') {
          job[col.column] = rawValue != null && rawValue !== '' ? Number(rawValue) : '';
        } else {
          job[col.column] = this.trimValue(rawValue);
        }
      });

      // Skip rows without any meaningful content
      const hasContent = cols.some(col => {
        const idx = colIndexMap[col.column];
        if (idx == null) return false;
        const val = row[idx];
        return val != null && String(val).trim() !== '';
      });
      if (!hasContent) return null;

      // ── Employee resolution ──────────────────────────────────────────
      this.resolveEmployee(job, fuseByName, fuseBySurname);

      // ── Dropdown validation ──────────────────────────────────────────
      this.validateDropdowns(job);

      // ── Resolve codes from descriptions ──────────────────────────────
      this.resolveCodes(job);

      // Determine final status
      if (job._messages.some(m => m.startsWith('ERROR:'))) {
        job._status = 'error';
        job._messages = job._messages.map(m => m.replace(/^ERROR:\s*/, ''));
      } else if (job._messages.some(m => m.startsWith('WARN:'))) {
        job._status = 'warning';
        job._messages = job._messages.map(m => m.replace(/^WARN:\s*/, ''));
      } else {
        job._status = 'ok';
      }

      return job;
    },

    // ── Employee lookup ────────────────────────────────────────────────
    resolveEmployee(job, fuseByName, fuseBySurname) {
      const persnr = this.trimValue(job.Mitarbeiternr);

      if (persnr) {
        // PSL-Nr provided: exact match
        const employee = this.allUsers.find(u => u.persnr === persnr);
        if (employee) {
          job.Mitarbeiternr = employee.persnr;
          job.Mitarbeiter_Vorname = employee.name;
          job.Mitarbeiter_Nachname = employee.surname;
        } else {
          job._messages.push(`ERROR: Mitarbeiter nicht gefunden: ${persnr}`);
        }
        return;
      }

      // No PSL-Nr: try fuzzy match by name
      const vorname = this.trimValue(job.Mitarbeiter_Vorname);
      const nachname = this.trimValue(job.Mitarbeiter_Nachname);

      if (!vorname && !nachname) return;

      // First try exact match (case-insensitive, trimmed)
      const exactMatch = this.allUsers.find(u => {
        if (!u.name || !u.surname) return false;
        return u.name.toLowerCase() === vorname.toLowerCase() &&
               u.surname.toLowerCase() === nachname.toLowerCase();
      });

      if (exactMatch) {
        job.Mitarbeiternr = exactMatch.persnr;
        job.Mitarbeiter_Vorname = exactMatch.name;
        job.Mitarbeiter_Nachname = exactMatch.surname;
        return;
      }

      // Fuzzy search: search vorname and nachname separately against their respective keys
      const nameResults = vorname ? fuseByName.search(vorname) : [];
      const surnameResults = nachname ? fuseBySurname.search(nachname) : [];

      // Combine results: collect all candidates with their match scores
      const candidates = new Map();
      nameResults.forEach(r => {
        const persnr = r.item.persnr;
        candidates.set(persnr, { item: r.item, nameScore: r.score, surnameScore: null });
      });
      surnameResults.forEach(r => {
        const persnr = r.item.persnr;
        if (candidates.has(persnr)) {
          candidates.get(persnr).surnameScore = r.score;
        } else {
          candidates.set(persnr, { item: r.item, nameScore: null, surnameScore: r.score });
        }
      });

      if (candidates.size === 0) {
        const attr = vorname || nachname;
        job._messages.push(`ERROR: Mitarbeiter nicht gefunden: ${attr}`);
        return;
      }

      // Score: average of available scores (lower is better in Fuse.js)
      const ranked = Array.from(candidates.values()).map(c => {
        const scores = [c.nameScore, c.surnameScore].filter(s => s != null);
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        return { item: c.item, score: avg, matchedBoth: c.nameScore != null && c.surnameScore != null };
      });

      // Prefer candidates that matched both name and surname, then by best score
      ranked.sort((a, b) => {
        if (a.matchedBoth !== b.matchedBoth) return a.matchedBoth ? -1 : 1;
        return a.score - b.score;
      });

      // Among top matches with same persnr prefix, prefer CSP over PSL
      let best = ranked[0].item;
      const topPersnrs = ranked.filter(r => r.matchedBoth === ranked[0].matchedBoth).map(r => r.item);
      for (const item of topPersnrs) {
        if (item.persnr && item.persnr.startsWith('CSP')) {
          best = item;
          break;
        }
      }

      // Check if the match is exact after trimming (case-insensitive)
      const isExact = best.name && best.surname &&
        best.name.toLowerCase() === vorname.toLowerCase() &&
        best.surname.toLowerCase() === nachname.toLowerCase();

      job.Mitarbeiternr = best.persnr;
      job.Mitarbeiter_Vorname = best.name;
      job.Mitarbeiter_Nachname = best.surname;

      if (!isExact) {
        job._messages.push(
          `WARN: Name abweichend – bitte prüfen. Vorlage: "${vorname} ${nachname}" → Stammdaten: "${best.name} ${best.surname}"`
        );
      }
    },

    // ── Dropdown field validation ──────────────────────────────────────
    validateDropdowns(job) {
      const vertrag = this.trimValue(job.Vertragsbeschreibung);

      if (vertrag && DROPDOWN_VALIDATORS.Vertragsbeschreibung) {
        const contract = this.allContracts.find(c => c.name === vertrag);
        if (!contract) {
          job._messages.push(`ERROR: Vertragsbeschreibung unbekannt: "${vertrag}"`);
        }
      }

      const firmenk = this.trimValue(job.Firmenkurzel);
      if (firmenk && this.allCompanies.length > 0) {
        if (!this.allCompanies.includes(firmenk)) {
          job._messages.push(`ERROR: Firmenkürzel unbekannt: "${firmenk}"`);
        }
      }

      const leistungsart = this.trimValue(job.Leistungsart_Code);
      if (leistungsart && this.allServicetypes.length > 0) {
        if (!this.allServicetypes.includes(leistungsart)) {
          job._messages.push(`ERROR: Leistungsart unbekannt: "${leistungsart}"`);
        }
      }
    },

    // ── Resolve Einsatzort/Einsatzstelle codes from descriptions ───────
    // Mirrors the manual setContract → setLocation → setPlace flow:
    //   - Belegnr from contract.code
    //   - Auto-fill Einsatzort if contract has exactly 1 location
    //   - Auto-fill Einsatzstelle if location has exactly 1 place
    //   - Error on unknown Einsatzort/Einsatzstelle values
    resolveCodes(job) {
      const vertrag = this.trimValue(job.Vertragsbeschreibung);
      const ortBeschr = this.trimValue(job.Einsatzort_Beschreibung);
      const stelleBeschr = this.trimValue(job.Einsatzstelle_Beschreibung);

      if (!vertrag) return;
      const contract = this.allContracts.find(c => c.name === vertrag);
      if (!contract) return;

      // Belegnr from contract (always set, like setContract does)
      if (contract.code) {
        job.Belegnr = contract.code;
      }

      const locations = contract.locations || [];

      // Auto-fill Einsatzort if only 1 location and none specified
      let effectiveOrt = ortBeschr;
      if (!effectiveOrt && locations.length === 1) {
        effectiveOrt = locations[0].name;
        job.Einsatzort_Beschreibung = locations[0].name;
      }

      if (!effectiveOrt) return;

      const location = locations.find(l => l.name === effectiveOrt);
      if (!location) {
        job._messages.push(`ERROR: Einsatzort unbekannt: "${effectiveOrt}"`);
        return;
      }

      job.Einsatzort_Code = location.code;

      const places = location.places || [];

      // Auto-fill Einsatzstelle if only 1 place and none specified
      let effectiveStelle = stelleBeschr;
      if (!effectiveStelle && places.length === 1) {
        effectiveStelle = places[0].name;
        job.Einsatzstelle_Beschreibung = places[0].name;
      }

      if (!effectiveStelle) return;

      const place = places.find(p => p.name === effectiveStelle);
      if (place) {
        job.Einsatzstelle_Code = place.code;
      } else {
        job._messages.push(`ERROR: Einsatzstelle unbekannt: "${effectiveStelle}"`);
      }
    },

    // ── Import confirmation ────────────────────────────────────────────
    confirmImport() {
      if (this.hasErrors) return;
      this.$emit('import', this.importedJobs);
      this.$emit('close');
    },

    // ── Value parsing helpers ──────────────────────────────────────────
    parseCheckbox(value) {
      if (!value) return false;
      const s = String(value).trim().toLowerCase();
      return s === 'x' || s === 'ja' || s === 'true' || s === '1';
    },

    parseDate(value) {
      if (!value) return '';
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      if (typeof value === 'number') {
        const date = XLSX.SSF.parse_date_code(value);
        return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
      }
      if (typeof value === 'string' && value.includes('.')) {
        const parts = value.split('.');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      return value != null ? String(value) : '';
    },

    parseTime(value, isEndTime = false) {
      if (!value) return '';
      if (value instanceof Date) {
        const hours = value.getHours();
        const minutes = value.getMinutes();
        if (isEndTime && hours === 0 && minutes === 0) return '23:59';
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
      if (typeof value === 'number') {
        const totalMinutes = Math.round(value * 24 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (isEndTime && hours === 0 && minutes === 0) return '23:59';
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
      if (typeof value === 'object' && value.result !== undefined) return '';
      return value != null ? String(value) : '';
    },

    trimValue(value) {
      if (!value) return '';
      if (typeof value === 'string') return value.trim();
      return value;
    }
  }
};
</script>

<style scoped>
</style>
